import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { ROUTES } from "../../../shared/utils";
import { integrationApi, SaveFitbitUserRequest } from "../api/integration";
import { AnalyticsEvent, trackEvent } from "../../../lib/analytics";

export const FitbitCallback: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get("code");
    const state = params.get("state");
    const oauthError = params.get("error");

    if (oauthError) {
      setError(`Fitbit authorisation error: ${oauthError}`);
      setProcessing(false);
      return;
    }

    const savedState = sessionStorage.getItem("fitbit_oauth_state");

    if (!state || state.length < 8) {
      setError("Invalid OAuth state returned — please reconnect.");
      setProcessing(false);
      return;
    }
    if (!savedState || savedState !== state) {
      setError("OAuth state mismatch — please try again.");
      setProcessing(false);
      return;
    }

    if (!code) {
      setError("Missing authorisation code — please reconnect.");
      setProcessing(false);
      return;
    }

    const exchangeAndSave = async () => {
      try {
        const tokenData = await integrationApi.exchangeFitbitToken(
          code,
          window.location.origin + "/fitbit-callback"
        );
        const { accessToken, refreshToken, expiresIn, profile, tokenType, scope } =
          tokenData.data;

        const saveData: SaveFitbitUserRequest = {
          accessToken,
          refreshToken,
          tokenType,
          scope,
          expiresAt: dayjs().add(expiresIn, "seconds").format(),
          profile: {
            id: profile?.encodedId || profile?.user_id || profile?.id,
            firstName: profile?.firstName,
            lastName: profile?.lastName,
            email: profile?.email,
          },
        };

        await integrationApi.saveFitbitUser(saveData);

        trackEvent(AnalyticsEvent.DeviceConnected, { provider: "fitbit" });

        navigate(ROUTES.COMPLETE, {
          state: { deviceConnected: true, provider: "fitbit" },
          replace: true,
        });
      } catch (err: any) {
        console.error("Fitbit OAuth error:", err);
        setError(
          err?.response?.data?.error_description ||
            err?.message ||
            "Unable to connect to Fitbit at the moment."
        );
      } finally {
        setProcessing(false);
      }
    };

    exchangeAndSave();
  }, [location.search, navigate]);

  const handleRetry = () => navigate(ROUTES.CONNECT_DEVICE);
  const handleGoDashboard = () => navigate(ROUTES.DASHBOARD);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: { xs: 3, md: 6 },
      }}
    >
      <Card
        variant="outlined"
        sx={{
          width: "100%",
          maxWidth: 520,
          borderRadius: 4,
          p: { xs: 4, md: 5 },
          textAlign: "center",
        }}
      >
        <Stack spacing={3} alignItems="center">
          {processing && (
            <>
              <CircularProgress />
              <Typography variant="body1">
                Finalising your Fitbit connection…
              </Typography>
            </>
          )}

          {!processing && error && (
            <>
              <Alert severity="error" sx={{ width: "100%" }}>
                {error}
              </Alert>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Button variant="contained" onClick={handleRetry}>
                  Try again
                </Button>
                <Button variant="outlined" onClick={handleGoDashboard}>
                  Go to dashboard
                </Button>
              </Stack>
            </>
          )}
        </Stack>
      </Card>
    </Box>
  );
};
